import React from "react";
import PropTypes from "prop-types";
import EntityProxy from "data/util/EntityProxy";
import {
    getTopLevelRecords,
    TOP_LEVEL_RECORDS_KEY,
} from "data/selectors/superRecords";
import TableStore from "data/TableStore";
import {
    ADD_NONE,
    ADD_TOP,
} from "lib/common/descriptorcontrols/BaseControls/RecordGrid/RecordGrid";
import ActionButton
    from "lib/common/descriptorcontrols/BaseControls/ActionButton";
import Checkbox from "lib/common/controls/Checkbox";
import RecordActions, { OPT_USER_INITIATED } from "data/RecordActions";
import SuperRecordActions from "data/SuperRecordActions";
import NavigationActions from "data/NavigationActions";
import Dispatcher from "Dispatcher";
import ClientId from "data/ClientId";
import { renderer } from "lib/dataentry/lib/dataentryform/formitems/resolver";
import isFieldHidden from "data/util/isFieldHidden";
import entityFieldMetaStore from "data/meta/entityFieldMetaStore";

class RecordGridDataGateway extends React.PureComponent {

    constructor(props) {
        super(...arguments);
        this.singularLabel = props.singularLabel || "Record" ;

        this.onAddClick = this.onAddClick.bind(this);
        this.onHeaderClick = this.onHeaderClick.bind(this);
        this.onRemoveClick = this.onRemoveClick.bind(this);
        this.onViewClick = this.onViewClick.bind(this);
        this.onFocus = this.onFocus.bind(this);
        this.onBlur = this.onBlur.bind(this);
        this.onKeyDown = this.onKeyDown.bind(this);
    }

    getCoords() {
        const {activeRecord, shortname, colCoords} = this.props;
        let coords = colCoords;
        if (coords == null) {
            coords = {
                record: activeRecord,
                fieldKey: shortname,
            };
        } else if (coords instanceof Array || coords instanceof Backbone.Collection) {
            throw new Error("Don't pass a collection to RecordGrid, pass coordinates");
        }
        return coords;
    }

    onAddClick(creatable, afterRecord) {
        const {
            fluxContext,
            allowView = false,
        } = this.props;

        const selfCoords = this.getCoords();
        const coords = creatable.colCoords ? creatable.colCoords : selfCoords;
        if (creatable.collection && ! creatable.colCoords) {
            console.log("creatable:", creatable.toJSON());
            throw new Error("Creatable w/ collection but not coords");
        }

        const records = recordsFromCoords(coords);
        const navigate = (selfCoords.record.entityKey === coords.record.entityKey)
                         && (selfCoords.fieldKey === coords.fieldKey) ? allowView : true;

        let insertAt = undefined;
        if (afterRecord != null) {
            // this works. not like how you'd think, but it does work.
            records.forEach((it, i) => {
                if (it.id === afterRecord.id) {
                    insertAt = i + 1;
                }
            });
        }
        const newId = ClientId.next();
        Dispatcher.contextDispatch(fluxContext, {
            type: RecordActions.NEW_CHILD,
            recordKey: coords.record.entityKey,
            recordId: coords.record.id,
            fieldKey: coords.fieldKey,
            entityKey: creatable.id,
            newId: newId,
            navigate,
            insertAt,
        });
    }

    onViewClick(r) {
        const { fluxContext } = this.props;
        Dispatcher.contextDispatch(fluxContext, {
            type: NavigationActions.SELECT_RECORD,
            entityKey: r.entityKey,
            recordId: r.id,
        });
    }

    onHeaderClick(r) {
        /* noop */
    }

    onRemoveClick(r) {
        const { fluxContext } = this.props;
        const coords = this.getCoords();
        const action = {
            type: RecordActions.DELETE_CHILD,
            recordKey: coords.record.entityKey,
            recordId: coords.record.id,
            fieldKey: coords.fieldKey,
            childKey: r.entityKey,
            childId: r.id,
        };

        Dispatcher.contextDispatch(fluxContext, action);
    }

    onFocus(e,d,v,k) {
        const { onFocus } = this.props;
        onFocus && onFocus(e, k);
    }

    onBlur(e) {
        const { onBlur } = this.props;
        onBlur && onBlur(e);
    }

    onKeyDown(e) {
        const { onKeyDown } = this.props;
        onKeyDown && onKeyDown(e);
    }

    getRecords() {
        return recordsFromCoords(
            this.getCoords()
        );
    }

    render() {
        const {
            component: Component,
            descriptor,
            controlOnly,
            enabled,
            addMode,
            creatables,
            columns: rawColumns,
            allowView,
            allowCreate,
            allowRemove,
        } = this.props;
        const coords = this.getCoords();
        let columns = rawColumns || [];
        if (coords.fieldKey !== TOP_LEVEL_RECORDS_KEY) {
            const efi = entityFieldMetaStore.getInfo(coords.record.entityKey, coords.fieldKey);
            columns = columns.filter(col => {
                if (col.type === "script") return true;
                if (!col.hasOwnProperty("fieldKey")) return true;
                return !isFieldHidden(efi.otherEntityKey, col.fieldKey);
            });
        }
        return (
            <Component
                key={descriptor.id} // react requires a unique key for all iterators
                descriptor={descriptor} // set the descriptor
                controlOnly={controlOnly}

                enabled={enabled !== undefined ? enabled : true}

                // grid structure
                addMode={creatables.length ? (addMode || ADD_TOP) : ADD_NONE}
                records={this.getRecords() || []}
                creatables={creatables || []}
                columns={columns}

                // allowed actions
                allowView={allowView !== undefined ? allowView : true}
                allowCreate={allowCreate !== undefined ? allowCreate : true}
                allowRemove={allowRemove !== undefined ? allowRemove : true}

                // actions
                onRemoveClick={((e, d, record) => this.onRemoveClick(record))}
                onAddClick={((e, d, creatable, afterRecord) => this.onAddClick(creatable, afterRecord))}
                onHeaderClick={((e, d, column, i) => this.onHeaderClick(column))}
                onViewClick={((e, d, record, i) => this.onViewClick(record))}
                onKeyDown={this.onKeyDown}

                // other events
                onFocus={this.onFocus}
                onBlur={this.onBlur}
            />
        );
    }
}

RecordGridDataGateway.propTypes = {
    descriptor: PropTypes.object.isRequired,
    fluxContext: PropTypes.string.isRequired,
    activeRecord: PropTypes.instanceOf(EntityProxy).isRequired,
    component: PropTypes.oneOfType([PropTypes.object, PropTypes.func]).isRequired,
    shortname: PropTypes.string.isRequired,
    singularLabel: PropTypes.string,
    colCoords: PropTypes.shape({
        record: PropTypes.any,
        fieldKey: PropTypes.string,
    }),

    controlOnly: PropTypes.bool,
    enabled: PropTypes.bool,
    addMode: PropTypes.string,
    allowView: PropTypes.bool,
    allowCreate: PropTypes.bool,
    allowRemove: PropTypes.bool,
    columns: PropTypes.arrayOf(PropTypes.shape({
        id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
        content: PropTypes.func.isRequired, /* column render function given the record data */
        label: PropTypes.any,
        width: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    })),
    records: PropTypes.arrayOf(PropTypes.object),
    creatables: PropTypes.arrayOf(PropTypes.shape({
        id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        label: PropTypes.any.isRequired
    })).isRequired,
};

export default RecordGridDataGateway;

export class MagicRecordGridDataGateway extends RecordGridDataGateway {
    onAddClick(creatable) {
        const { fluxContext } = this.props;
        const coords = this.getCoords();
        const navigate = true;
        const action = {
            type: SuperRecordActions.NEW_MAGIC_CHILD,
            recordKey: coords.record.entityKey,
            recordId: coords.record.id,
            entityKey: creatable.id,
            newId: ClientId.next(),
            navigate,
        };
        Dispatcher.contextDispatch(fluxContext, action);
    }

    onRemoveClick(r) {
        const { fluxContext } = this.props;
        const action = {
            type: RecordActions.DELETE_RECORD,
            recordKey: r.entityKey,
            recordId: r.id,
        };
        Dispatcher.contextDispatch(fluxContext, action);
    }
}

MagicRecordGridDataGateway.propTypes = {
    ...RecordGridDataGateway.propTypes,
    activeRecord: PropTypes.shape({
        entityKey: PropTypes.string.isRequired,
        id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
        topLevelRecords: PropTypes.array
    }).isRequired,
};

const recordsFromCoords = colCoords =>
    colCoords.fieldKey === TOP_LEVEL_RECORDS_KEY ?
        colCoords.record.hasOwnProperty("topLevelIds") ?
            getTopLevelRecords(TableStore, colCoords.record) :
            colCoords.record.topLevelRecords :
        colCoords.record.get(colCoords.fieldKey);

export const buildCreatable = (implKey, label, id, collectionKey = null, activeRecord = null) => {
    const creatable = {
        id,
        implKey: implKey,
        label,
        enabled: true,
    };

    if (collectionKey != null) {
        const coords = {
            record: activeRecord,
            fieldKey: collectionKey,
        };
        creatable.colCoords = coords;
        creatable.collection = recordsFromCoords(coords);
    }

    if (activeRecord) {
        // TODO: DLG creatable enabled state is not correct but so far nothing
        // is relying on this functionality.
        // we need each row and column add action to have their own enabled
        // state.
        creatable.enabled = activeRecord.isFieldEnabled(collectionKey);
    }
    return creatable;
};

export const buildColumn = (column) => ({
        id: "",
        content: (record) => {/* noop */},
        label: "",
        width: undefined,
        ...column
    });

export const buildColumnsFromConfig = (configColumns = [], props) =>
    configColumns.map((col, i) => {
        const c = {
            id: i,
            label: col.heading,
            width: col.width,
        };
        if (col.itemInfo) {
            c.fieldKey = col.itemInfo.key;
        }

        if (col.type === "script") {
            c.content = (record) => (
                    <div
                        key={record.id}
                        className={`control-container-expr-${i} control expression-field`}>
                        {
                            /* eslint-disable-next-line no-new-func */
                            Function("instance", col.script)(record)
                        }
                    </div>
                );
        } else {
            const descriptor = col.itemInfo; // descriptor
            c.content = record => renderer({
                ...props,
                descriptor: {
                    ...descriptor,
                    // overload the descriptor with new id for this cell
                    id: `${descriptor.id}r${record.id || record.cid}`
                },
                activeRecord: record,
                controlOnly: true
            });
        }
        return buildColumn(c);
    });

export const buildDevicelesiongridCheckboxColumns = (props, columnRecords = null) => {
    const {fluxContext, activeRecord, descriptor, onFocus, onBlur, onKeyDown} = props;
    if (columnRecords == null) {
        columnRecords = activeRecord.get(descriptor.key);
    }
    const config = descriptor.config || {};
    const rowMembershipCollectionKey = config.rowSourceEntityCollectionKey;
    const canWrite = activeRecord.canWrite();

    const onViewClick = (r) => {
        Dispatcher.contextDispatch(fluxContext, {
            type: NavigationActions.SELECT_RECORD,
            entityKey: r.entityKey,
            recordId: r.id,
        });
    };

    const columnAddFn = (columnRecord) => {
        const columnLabel = columnRecord.getLabel();

        return buildColumn({
            id: columnRecord.id,
            entityContext: columnRecord.entityKey,
            width: "100",
            align: "center",
            label: (
                <React.Fragment>
                    {columnLabel}
                    {" "}
                    <ActionButton actionType="view"
                                  focusable={false}
                                  onClick={() => onViewClick(columnRecord)} />
                </React.Fragment>
            ),
            content: (rowRecord) => {
                const coll = columnRecord.get(rowMembershipCollectionKey);
                const columnProps = {
                    id: `${descriptor.id}r${rowRecord.id || rowRecord.cid}c${columnRecord.id || columnRecord.cid}`,
                    shortname: rowMembershipCollectionKey,
                    enabled: canWrite && columnRecord.isFieldEnabled(rowMembershipCollectionKey),
                    value: coll != null && coll.some(r => r.id === rowRecord.id),
                    onCommitChange: v =>
                        Dispatcher.contextDispatch(fluxContext, {
                            type: RecordActions.SET_FIELD,
                            recordKey: columnRecord.entityKey,
                            recordId: columnRecord.id,
                            fieldKey: rowMembershipCollectionKey,
                            value: v
                                ? v => v.concat(rowRecord.id)
                                : v => v.filter(rID => rID !== rowRecord.id),
                            [OPT_USER_INITIATED]: true,
                        }),
                    onFocus: (e,v,k) => { // (event, descriptor, value, keyboardShortcuts)
                        // moveFocus expects the target to be the .react-control which has the field id
                        e.currentTarget = $(e.currentTarget).parents(".react-control").get(0);
                        onFocus && onFocus(e, k);
                    },
                    onBlur: (e) => { // (event, descriptor, value)
                        // moveFocus expects the target to be the .react-control which has the field id
                        e.currentTarget = $(e.currentTarget).parents(".react-control").get(0);
                        onBlur && onBlur(e);
                    },
                    onKeyDown: (e) => { // (event, descriptor, value, isKeyboardShortcut)
                        onKeyDown && onKeyDown(e);
                    }
                };
                return (
                    <Checkbox
                        controlOnly={true}
                        {...columnProps}
                    />
                );
            }
        });
    };

    return columnRecords.map(columnAddFn);
};



// WEBPACK FOOTER //
// ./app/lib/dataentry/lib/dataentryform/formitems/RecordGridDataGateway.js